import { Link } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { FaPhone, FaEnvelope, FaChevronRight, FaFacebook, FaInstagram, FaYoutube, FaDraftingCompass, FaHardHat, FaWindowRestore, FaHome } from 'react-icons/fa'
import { useTranslation, Trans } from 'react-i18next'
import { getSubdomainUrl, getCurrentBU } from '../utils/links'
import { supabase } from '../lib/supabase'

const businessUnits = [
  { label: 'nav.architecture', subdomain: 'architecture', icon: FaDraftingCompass },
  { label: 'nav.construction', subdomain: 'construction', icon: FaHardHat },
  { label: 'nav.windows', subdomain: 'windows', icon: FaWindowRestore },
  { label: 'nav.roofing', subdomain: 'roofing', icon: FaHome },
  // { label: 'Real Estates' },
  // { label: 'Academy' },
  // { label: 'Franchise' },
]

const licenses = {
  Florida: ['DBPR- AR102701', 'DBPR - 35922', 'DBPR - CGC1538980', 'DBPR - CCC1337084'],
  Texas: ['TBPE Firm - 22854'],
}

// Cache TTL in milliseconds (1 hour)
const PORTFOLIO_CACHE_TTL = 60 * 60 * 1000

const Footer = () => {
  const { t } = useTranslation()
  const [portfolioUrl, setPortfolioUrl] = useState(null)
  const [currentBusinessUnit, setCurrentBusinessUnit] = useState('main')

  // Fetch portfolio URL based on current subdomain with localStorage caching
  useEffect(() => {
    async function fetchPortfolio() {
      try {
        const businessUnit = getCurrentBU()
        setCurrentBusinessUnit(businessUnit)

        // Check localStorage cache first
        const cacheKey = `portfolio_${businessUnit}`
        const cached = localStorage.getItem(cacheKey)
        if (cached) {
          try {
            const { url, timestamp } = JSON.parse(cached)
            // Use cache if less than 1 hour old
            if (Date.now() - timestamp < PORTFOLIO_CACHE_TTL) {
              setPortfolioUrl(url)
              return
            }
          } catch {
            // Invalid cache, proceed to fetch
          }
        }

        // Try to get portfolio for current business unit
        let { data } = await supabase
          .from('portfolios')
          .select('file_url')
          .eq('business_unit', businessUnit)
          .eq('is_active', true)
          .single()

        // If not found and not main, fall back to main portfolio
        if (!data && businessUnit !== 'main') {
          const { data: mainData } = await supabase
            .from('portfolios')
            .select('file_url')
            .eq('business_unit', 'main')
            .eq('is_active', true)
            .single()
          data = mainData
        }

        const url = data?.file_url || null
        // Convert Supabase URL to proxied domain URL for cleaner appearance
        const displayUrl = url ? `/portfolio/${url.split('/portfolios/')[1]}` : null
        setPortfolioUrl(displayUrl)

        // Cache the display URL (proxied version)
        if (displayUrl) {
          localStorage.setItem(cacheKey, JSON.stringify({ url: displayUrl, timestamp: Date.now() }))
        }
      } catch (error) {
        // Silently fail - portfolio is optional
        console.log('Portfolio fetch error:', error)
      }
    }
    fetchPortfolio()
  }, [])

  return (
    <footer className="bg-black border-t border-gray-700 pt-12 pb-8 min-h-[500px]" aria-labelledby="footer-heading">
      <h2 id="footer-heading" className="sr-only">Footer</h2>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-4">
          {/* Column 1: Logo & Description */}
          <div>
            <div className="mb-6 flex justify-center">
              <img
                src="/logo-black.webp"
                alt="JD Rivero logo"
                width="96"
                height="96"
                className="h-24 w-24 object-contain invert grayscale brightness-200"
                loading="lazy"
              />
            </div>
            <p className="mb-6 text-sm text-gray-300 leading-relaxed text-justify">
              <Trans i18nKey="footer.description" components={{ 1: <strong className="text-white" /> }} />
            </p>
            <div className="flex gap-4">
              <a href="https://www.facebook.com/jdriveroinc/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Visit us on Facebook">
                <FaFacebook className="text-xl" />
              </a>
              <a href="https://www.instagram.com/stories/highlights/18076407466817371/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Visit us on Instagram">
                <FaInstagram className="text-xl" />
              </a>
              <a href="https://www.youtube.com/@jdrivero4394" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Visit us on YouTube">
                <FaYoutube className="text-xl" />
              </a>
            </div>
            <p className="mt-6 text-xs text-gray-300">
              © {new Date().getFullYear()} JD Rivero. {t('footer.rights_reserved')} | <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">{t('footer.terms_of_service')}</Link> | <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">{t('footer.privacy_policy')}</Link> | <Link to="/sitemap" className="hover:text-primary transition-colors">{t('footer.sitemap')}</Link>
            </p>
          </div>

          {/* Column 2: Business Units */}
          <div>
            <h3 className="text-lg font-bold text-white mb-6">{t('footer.business_units').toUpperCase()}</h3>
            <ul className="space-y-4">
              {businessUnits.map((unit) => {
                const url = unit.subdomain ? getSubdomainUrl(unit.subdomain) : '#'
                const Icon = unit.icon
                return (
                  <li key={unit.label}>
                    {unit.subdomain ? (
                      url.startsWith('http') ? (
                      <a
                        href={url}
                        className="group flex items-center text-gray-400 transition-colors hover:text-primary focus:outline-none"
                      >
                        <div className="relative flex items-center justify-center w-5 h-5 mr-3">
                          <FaChevronRight className="absolute h-3 w-3 opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-primary" />
                          <Icon className="h-4 w-4 transition-all duration-300 group-hover:opacity-0 group-hover:translate-x-2" />
                        </div>
                        <span className="text-sm font-semibold uppercase tracking-[0.1em]">{t(unit.label)}</span>
                      </a>
                      ) : (
                      <Link
                        to={url}
                        className="group flex items-center text-gray-400 transition-colors hover:text-primary focus:outline-none"
                      >
                        <div className="relative flex items-center justify-center w-5 h-5 mr-3">
                          <FaChevronRight className="absolute h-3 w-3 opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0 group-hover:text-primary" />
                          <Icon className="h-4 w-4 transition-all duration-300 group-hover:opacity-0 group-hover:translate-x-2" />
                        </div>
                        <span className="text-sm font-semibold uppercase tracking-[0.1em]">{t(unit.label)}</span>
                      </Link>
                      )
                    ) : (
                      <div className="flex items-center text-gray-600 cursor-not-allowed">
                        <div className="relative flex items-center justify-center w-5 h-5 mr-3">
                          {Icon && <Icon className="h-4 w-4" />}
                        </div>
                        <span className="text-sm font-semibold uppercase tracking-[0.1em]">{t(unit.label)}</span>
                      </div>
                    )}
                  </li>
                )
              })}
            </ul>
          </div>

          {/* Column 3: Contact Us */}
          <div>
            <h4 className="mb-6 text-lg font-bold text-white">{t('footer.contact_us').toUpperCase()}</h4>
            <div className="space-y-4 text-sm text-gray-300">
              <a href="tel:+12144620683" className="flex items-center gap-3 hover:text-primary transition group">
                <FaPhone className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                <span><span className="text-gray-400 font-bold text-[10px] mr-2 uppercase tracking-tighter">{t('regions.texas')}</span>(214) 462 0683</span>
              </a>
              <a href="tel:+17862961439" className="flex items-center gap-3 hover:text-primary transition group">
                <FaPhone className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                <span><span className="text-gray-400 font-bold text-[10px] mr-2 uppercase tracking-tighter">{t('regions.florida')}</span>(786) 296 1439</span>
              </a>
              <a href="mailto:info@jdrivero.com" className="flex items-center gap-3 hover:text-primary transition group">
                <FaEnvelope className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
                <span>info@jdrivero.com</span>
              </a>
              {portfolioUrl && currentBusinessUnit !== 'main' && (
                <a href={portfolioUrl} target="_blank" rel="noopener noreferrer" className="block w-fit mt-4 px-6 py-2 border border-primary text-primary hover:bg-primary hover:text-black font-bold uppercase text-xs tracking-widest transition-all rounded-full text-center">
                  {t(`footer.portfolio_buttons.${currentBusinessUnit}`)}
                </a>
              )}
            </div>
          </div>

          {/* Column 4: Licenses */}
          <div>
            <h4 className="mb-6 text-lg font-bold text-white">{t('footer.licenses_title')}</h4>
            <div className="space-y-6 text-sm text-gray-300">
              {Object.entries(licenses).map(([region, items]) => (
                <div key={region}>
                  <h5 className="text-primary font-bold mb-2">{region}</h5>
                  <ul className="space-y-1 text-gray-400">
                    {items.map((license) => (
                      <li key={license}>{license}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
